class NuraHeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.timer = null;
    this.pointerStartX = null;
    this.isPaused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.slides = Array.from(this.querySelectorAll('[data-nura-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-nura-dot]'));
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = Math.max(Number(this.dataset.interval) || 7000, 4000);

    if (!this.slides.length) return;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.show(Number(dot.dataset.nuraDot), true));
    });

    this.addEventListener('pointerenter', () => this.pause());
    this.addEventListener('pointerleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume();
    });
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      this.pointerStartX = event.clientX;
    });
    this.addEventListener('pointerup', (event) => this.handleSwipe(event));
    this.addEventListener('pointercancel', () => {
      this.pointerStartX = null;
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.show(0, false);
    this.startTimer();
    this.dataset.initialized = 'true';
  }

  disconnectedCallback() {
    this.stopTimer();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  show(nextIndex, userInitiated = false) {
    if (!this.slides.length) return;

    const normalizedIndex = (nextIndex + this.slides.length) % this.slides.length;
    this.currentIndex = normalizedIndex;

    this.slides.forEach((slide, index) => {
      const isActive = index === normalizedIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (isActive) {
          if (element.dataset.nuraPreviousTabindex !== undefined) {
            const previousTabindex = element.dataset.nuraPreviousTabindex;
            if (previousTabindex === '') element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', previousTabindex);
            delete element.dataset.nuraPreviousTabindex;
          } else if (element.getAttribute('tabindex') === '-1') {
            element.removeAttribute('tabindex');
          }
        } else if (element.getAttribute('tabindex') !== '-1') {
          element.dataset.nuraPreviousTabindex = element.getAttribute('tabindex') || '';
          element.setAttribute('tabindex', '-1');
        }
      });
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === normalizedIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (userInitiated) this.restartTimer();
  }

  handleSwipe(event) {
    if (this.pointerStartX === null) return;
    const distance = event.clientX - this.pointerStartX;
    this.pointerStartX = null;

    if (Math.abs(distance) < 45) return;
    this.show(distance > 0 ? this.currentIndex - 1 : this.currentIndex + 1, true);
  }

  startTimer() {
    if (!this.autoplay || this.slides.length < 2 || this.reducedMotion.matches || this.isPaused || document.hidden) return;
    this.stopTimer();
    this.timer = window.setInterval(() => this.show(this.currentIndex + 1), this.interval);
  }

  stopTimer() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartTimer() {
    this.stopTimer();
    this.startTimer();
  }

  pause() {
    this.isPaused = true;
    this.classList.add('is-paused');
    this.stopTimer();
  }

  resume() {
    this.isPaused = false;
    this.classList.remove('is-paused');
    this.startTimer();
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.classList.add('is-paused');
      this.stopTimer();
    } else {
      if (!this.isPaused) this.classList.remove('is-paused');
      this.startTimer();
    }
  }

  onBlockSelect(event) {
    const selectedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
    if (selectedIndex < 0) return;
    this.pause();
    this.show(selectedIndex, false);
  }
}

if (!customElements.get('nura-hero-carousel')) {
  customElements.define('nura-hero-carousel', NuraHeroCarousel);
}
